import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, LogOut, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

export default function LockScreen() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isLocked, setIsLocked] = useState(true);
  const [elapsedTime, setElapsedTime] = useState('0s');

  useEffect(() => {
    const lockedTime = new Date();
    // Update elapsed time every second
    const timer = setInterval(() => {
      const now = new Date();
      const diff = Math.floor((now - lockedTime) / 1000);

      if (diff < 60) {
        setElapsedTime(`${diff}s`);
      } else if (diff < 3600) {
        setElapsedTime(`${Math.floor(diff / 60)}m ${diff % 60}s`);
      } else {
        setElapsedTime(`${Math.floor(diff / 3600)}h`);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleUnlock = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Verify password with backend
      const response = await api.post('/auth/verify-password', {
        password: password
      });

      if (response.data.success) {
        setIsLocked(false);
        setPassword('');
        console.log('✅ Session unlocked successfully');
        // Redirect to dashboard after 1 second
        setTimeout(() => {
          navigate('/dashboard');
        }, 1000);
      }
    } catch (err) {
      // Handle different error types
      const errorCode = err.response?.data?.error;
      const errorMsg = err.response?.data?.message;

      if (err.response?.status === 401 && errorCode === 'SESSION_EXPIRED') {
        // Session expired - redirect to login with message
        console.warn('Session expired during unlock');
        setError('Your session expired. Redirecting to login...');
        setTimeout(() => {
          logout();
          navigate('/login', { state: { message: 'Session expired. Please log in again.' } });
        }, 1500);
      } else if (err.response?.status === 400 && errorCode === 'PASSWORD_INVALID') {
        // Password is wrong
        setError(errorMsg || 'Incorrect password. Please try again.');
        setPassword('');
      } else {
        // Generic error
        setError(errorMsg || 'Password verification failed. Please try again.');
        setPassword('');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-300">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-dark-900 dark:to-dark-800 px-4">
      <div className="w-full max-w-md">
        {/* Lock Icon */}
        <div className="flex justify-center mb-8">
          <div className="w-20 h-20 rounded-full bg-brand-50 dark:bg-brand-900/20 flex items-center justify-center">
            <Lock size={40} className="text-brand-600 dark:text-brand-400" />
          </div>
        </div>

        {/* Title */}
        <h1 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-2">
          Session Locked
        </h1>

        {/* User Info */}
        <div className="text-center mb-8">
          <p className="text-gray-600 dark:text-gray-300 mb-1">Welcome back,</p>
          <p className="text-lg font-semibold text-gray-900 dark:text-white">{user.name}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
            {user.email}
          </p>
          <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-3">
            Locked for {elapsedTime}
          </p>
        </div>

        {/* Unlock Form */}
        {isLocked ? (
          <form onSubmit={handleUnlock} className="space-y-4 mb-6">
            {/* Password Input */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Enter your password to continue
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoFocus
                  disabled={loading}
                  className="w-full px-4 py-3 bg-white dark:bg-dark-700 border border-gray-300 dark:border-dark-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:focus:ring-brand-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 disabled:opacity-50 transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              {/* Error Message */}
              {error && (
                <div className="mt-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/30 rounded-lg">
                  <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                </div>
              )}
            </div>

            {/* Unlock Button */}
            <button
              type="submit"
              disabled={!password || loading}
              className="w-full px-4 py-3.5 bg-brand-700 hover:bg-brand-800 dark:bg-brand-600 dark:hover:bg-brand-700 text-white font-semibold rounded-lg shadow-sm hover:shadow transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Unlocking...
                </>
              ) : (
                <>
                  <Lock size={18} />
                  Unlock Session
                </>
              )}
            </button>
          </form>
        ) : (
          // Success Message
          <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-900/30 rounded-lg mb-6">
            <p className="text-center text-sm text-green-600 dark:text-green-400 font-medium">
              ✓ Session unlocked successfully
            </p>
          </div>
        )}

        {/* Logout Button */}
        <button
          onClick={handleLogout}
          className="w-full px-4 py-3 border border-gray-300 dark:border-dark-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700 font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
        >
          <LogOut size={18} />
          Sign Out Instead
        </button>

        {/* Footer Info */}
        <div className="mt-8 p-3 bg-gray-50/80 dark:bg-dark-800/50 border border-dashed border-gray-200 dark:border-dark-700 rounded-lg">
          <p className="text-xs text-gray-400 dark:text-gray-500">
            💡 <span className="font-medium text-gray-500 dark:text-gray-400">Tip:</span> Your session is locked for your security. Enter your password to continue working.
          </p>
        </div>
      </div>
    </div>
  );
}
